// /Users/caffas/Documents/Sistemas/vsadmin/admin/admin-app/src/Dashboard.js
import React, { useState, useEffect, useCallback , useRef} from 'react';
import { Title } from 'react-admin';
import { Card, CardContent, Typography, Grid, Box, CircularProgress, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, FormControl, InputLabel, Select, MenuItem ,useTheme, useMediaQuery } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import DevicesIcon from '@mui/icons-material/Devices';
import TodayIcon from '@mui/icons-material/Today';
import RefreshIcon from '@mui/icons-material/Refresh';
import PercentIcon from '@mui/icons-material/Percent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import DemoIcon from '@mui/icons-material/PlayArrow';
import FilterListIcon from '@mui/icons-material/FilterList';
import SupportIcon from '@mui/icons-material/ContactSupport';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ProjectIcon from '@mui/icons-material/FolderCopyOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer , PieChart, Pie, Cell, LineChart, Line} from 'recharts';
import { CustomerAvatar } from "./Pages/Users/CustomerAvatar";


// src/Dashboard.js
// Agregar este componente después de los imports y antes de RecentUsersTable

const ResponsiveChartContainer = ({ children, height = 300 }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    // Ajustar altura según el dispositivo
    const responsiveHeight = isMobile ? 250 : isTablet ? 280 : height;
    
    return (
        <ResponsiveContainer 
            width="100%" 
            height={responsiveHeight}
            minHeight={200}
        >
            {children}
        </ResponsiveContainer>
    );
};

const RecentUsersTable = ({ data, isLoading }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const getLicenseColor = (tipoTrial) => {
        switch (tipoTrial?.toLowerCase()) {
            case 'trial': return 'warning';
            case 'premium': return 'success';
            case 'basic': return 'info';
            case 'enterprise': return 'secondary';
            case 'pago': return 'success';
            default: return 'default';
        }
    };

    const formatRegistrationDate = (dateStr) => {
        if (!dateStr) return 'Sin fecha';
        try {
            const date = new Date(dateStr);
            return isMobile 
                ? `${date.getDate()}/${date.getMonth() + 1}` // Formato corto en móvil
                : `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
        } catch (error) {
            return 'Fecha inválida';
        }
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
    };
    return (
        <Card sx={{ mt: 4, p: 2, boxShadow: 3 }}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={3}>
                    <PersonIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" gutterBottom>Últimos 10 Usuarios Registrados</Typography>
                </Box>
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Usuario</TableCell>
                                    <TableCell>Tipo Licencia</TableCell>
                                    <TableCell>Fecha License Start</TableCell>
                                    <TableCell>Fuente</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data && data.length > 0 ? (
                                    data.map((user, index) => (
                                        <TableRow key={user.id || index} hover>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    <CustomerAvatar size="small" />
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {user.name || user.username || 'Sin nombre'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={user.tipotrial || 'Sin licencia'} color={getLicenseColor(user.tipotrial)} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{formatRegistrationDate(user.created_at || user.fechaRegistro)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textSecondary">{user.source || user.fuente || 'Web'}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="body2" color="textSecondary">No hay usuarios para mostrar</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

const LogDayActivityUsersChart = ({ data, isLoading }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    return (
        <Card sx={{ mt: 4, p: { xs: 1, sm: 2 }, boxShadow: 3 }}>
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                    Actividad de Usuarios Únicos por Día
                </Typography>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <ResponsiveChartContainer>
                        <LineChart 
                            data={data} 
                            margin={{ 
                                top: 5, 
                                right: isMobile ? 10 : 30, 
                                left: isMobile ? 10 : 20, 
                                bottom: 5 
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="month" 
                                tickFormatter={(value) => {
                                    const date = new Date(value);
                                    return `${date.getDate()}/${date.getMonth() + 1}`;
                                }}
                                fontSize={isMobile ? 10 : 12}
                                interval={isMobile ? 1 : 0}
                            />
                            <YAxis fontSize={isMobile ? 10 : 12} />
                            <Tooltip 
                                labelFormatter={(value) => {
                                    const date = new Date(value);
                                    return `Fecha: ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
                                }} 
                                formatter={(value) => [value, 'Usuarios Activos']}
                                contentStyle={{ fontSize: isMobile ? '12px' : '14px' }}
                            />
                            <Legend wrapperStyle={{ fontSize: isMobile ? '12px' : '14px' }} />
                            <Line 
                                type="monotone" 
                                dataKey="countmonth" 
                                stroke="#4CAF50" 
                                strokeWidth={isMobile ? 2 : 3} 
                                dot={{ fill: '#4CAF50', strokeWidth: 2, r: isMobile ? 3 : 4 }} 
                                activeDot={{ r: isMobile ? 4 : 6, stroke: '#4CAF50', strokeWidth: 2 }} 
                                name="Usuarios" 
                            />
                        </LineChart>
                    </ResponsiveChartContainer>
                )}
            </CardContent>
        </Card>
    );
};

const LogDayActivityStatusChart = ({ data, isLoading }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // Estado para el filtro de status seleccionado
    const [selectedStatus, setSelectedStatus] = useState('all');
    
    const getStatusColor = (status) => {
        const statusCode = status.toString();
        if (statusCode === '200') return '#4CAF50';
        else if (statusCode === '500') return '#F44336';
        else return '#9E9E9E';
    };

    // Obtener todos los status únicos para el filtro
    const uniqueStatuses = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        return [...new Set(data.map(item => item.status))];
    }, [data]);

    // Filtrar datos según el status seleccionado
    const filteredData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        
        if (selectedStatus === 'all') {
            return data;
        }
        
        return data.filter(item => item.status.toString() === selectedStatus);
    }, [data, selectedStatus]);

    // Procesar datos para agrupar por fecha y crear barras por status
    const processedData = React.useMemo(() => {
        if (!filteredData || filteredData.length === 0) return [];
        
        const groupedByDate = filteredData.reduce((acc, item) => {
            const date = item.month;
            if (!acc[date]) acc[date] = { month: date };
            acc[date][`status_${item.status}`] = parseInt(item.countmonth) || 0;
            return acc;
        }, {});
        return Object.values(groupedByDate).sort((a, b) => new Date(a.month) - new Date(b.month));
    }, [filteredData]);

    // Obtener status únicos de los datos filtrados para crear las barras
    const uniqueStatusesForChart = React.useMemo(() => {
        if (!filteredData || filteredData.length === 0) return [];
        return [...new Set(filteredData.map(item => item.status))];
    }, [filteredData]);

    // Manejar cambio de filtro
    const handleStatusChange = (event) => {
        setSelectedStatus(event.target.value);
    };

    return (
        <Card sx={{ mt: 4, p: 2, boxShadow: 3 }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" gutterBottom>HTTP Status Code x Día</Typography>
                    
                    {/* Filtro de status */}
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel id="status-filter-label">
                            <Box display="flex" alignItems="center">
                                <FilterListIcon sx={{ mr: 1, fontSize: 16 }} />
                                Filtrar por Status
                            </Box>
                        </InputLabel>
                        <Select
                            labelId="status-filter-label"
                            value={selectedStatus}
                            onChange={handleStatusChange}
                            label="Filtrar por Status"
                        >
                            <MenuItem value="all">
                                <em>Todos los status</em>
                            </MenuItem>
                            {uniqueStatuses.map((status) => (
                                <MenuItem key={status} value={status.toString()}>
                                    Status {status}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Mostrar status seleccionado */}
                {selectedStatus !== 'all' && (
                    <Box mb={2}>
                        <Chip 
                            label={`Mostrando: Status ${selectedStatus}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            onDelete={() => setSelectedStatus('all')}
                        />
                    </Box>
                )}

                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <CircularProgress />
                    </Box>
                ) : processedData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <Typography variant="body2" color="textSecondary">
                            No hay datos disponibles para el status seleccionado
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={processedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tickFormatter={(value) => {
                                const date = new Date(value);
                                return `${date.getDate()}/${date.getMonth() + 1}`;
                            }} />
                            <YAxis />
                            <Tooltip labelFormatter={(value) => {
                                const date = new Date(value);
                                return `Fecha: ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
                            }} formatter={(value, name) => {
                                const statusCode = name.replace('status_', '');
                                return [value, `Status ${statusCode}`];
                            }} />
                            <Legend formatter={(value) => `Status ${value.replace('status_', '')}`} />
                            {uniqueStatusesForChart.map((status, index) => (
                                <Bar 
                                    key={status}
                                    dataKey={`status_${status}`} 
                                    fill={getStatusColor(status)} 
                                    name={`status_${status}`} 
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

// ... rest of code remains same


const LogDayActivityChart = ({ data, isLoading }) => (
    <Card sx={{ mt: 4, p: 2, boxShadow: 3 }}>
        <CardContent>
            <Typography variant="h6" gutterBottom>Actividad de Logs por Día</Typography>
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <CircularProgress />
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getDate()}/${date.getMonth() + 1}`;
                        }} />
                        <YAxis />
                        <Tooltip labelFormatter={(value) => {
                            const date = new Date(value);
                            return `Fecha: ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
                        }} formatter={(value) => [value, 'Actividad de Logs']} />
                        <Legend />
                        <Line type="monotone" dataKey="countmonth" stroke="#FF6B35" strokeWidth={3} dot={{ fill: '#FF6B35', strokeWidth: 2, r: 4 }} activeDot={{ r: 6, stroke: '#FF6B35', strokeWidth: 2 }} name="Logs Diarios" />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </CardContent>
    </Card>
);

const SourceDonutChart = ({ data, isLoading }) => {
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FF6B6B', '#6B66FF'];
    const getSourceColor = (source) => {
        const sourceLower = source.toLowerCase();
        if (sourceLower.includes('visualscale')) return '#808080';
        else if (sourceLower.includes('facebook')) return '#1877F2';
        else if (sourceLower.includes('google')) return '#DB4437';
        else {
            const index = data.findIndex(item => item.source === source);
            return COLORS[index % COLORS.length];
        }
    };
    return (
        <Card sx={{ mt: 4, p: 2, boxShadow: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>Distribución de Usuarios por Fuente</Typography>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={data} cx="50%" cy="50%" labelLine={true} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={100} innerRadius={60} fill="#8884d8" dataKey="count" nameKey="source">
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={getSourceColor(entry.source)} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name, props) => [`${value} usuarios`, props.payload.source]} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

const TipoLicenciaChart = ({ data, isLoading }) => {
    const COLORS = ['#4CAF50', '#FF9800', '#2196F3', '#F44336', '#9C27B0', '#00BCD4', '#CDDC39', '#795548'];
    return (
        <Card sx={{ mt: 4, p: 2, boxShadow: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>Distribución por Tipo de Licencia</Typography>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={data} cx="50%" cy="50%" labelLine={true} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={100} innerRadius={60} fill="#8884d8" dataKey="count" nameKey="tipoLicencia">
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name, props) => [`${value} usuarios`, props.payload.tipoLicencia]} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

const KpiCard = ({ icon, title, value, color }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    return (
        <Card sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            boxShadow: 3,
            minHeight: { xs: 100, sm: 120 }
        }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box display="flex" alignItems="center" mb={isMobile ? 1 : 2}>
                    <Box sx={{ 
                        backgroundColor: `${color}.light`, 
                        borderRadius: '50%', 
                        p: { xs: 0.5, sm: 1 }, 
                        mr: { xs: 1, sm: 2 }
                    }}>
                        {React.cloneElement(icon, { 
                            sx: { 
                                ...icon.props.sx, 
                                fontSize: { xs: 16, sm: 24 } 
                            } 
                        })}
                    </Box>
                    <Typography 
                        variant={isMobile ? "caption" : "h6"} 
                        color="textSecondary"
                        sx={{ 
                            fontSize: { xs: '0.75rem', sm: '1rem' },
                            lineHeight: { xs: 1.2, sm: 1.5 }
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
                <Typography 
                    variant={isMobile ? "h6" : "h4"} 
                    component="div" 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' } }}
                >
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );
};

const UserActivityChart = ({ data, isLoading }) => (
    <Card sx={{ mt: 4, p: 2, boxShadow: 3 }}>
        <CardContent>
            <Typography variant="h6" gutterBottom>Actividad de Usuarios por Día (ultimos 30 dias)</Typography>
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <CircularProgress />
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getDate()}/${date.getMonth() + 1}`;
                        }} />
                        <YAxis />
                        <Tooltip labelFormatter={(value) => {
                            const date = new Date(value);
                            return `Fecha: ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
                        }} />
                        <Legend />
                        <Bar dataKey="webcountmonth" fill="#8884d8" name="Usuarios Web" />
                        <Bar dataKey="gafascountmonth" fill="#82ca9d" name="Usuarios Meta" />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </CardContent>
    </Card>
);

const MonthlyUserChart = ({ data, isLoading }) => (
    <Card sx={{ mt: 4, p: 2, boxShadow: 3 }}>
        <CardContent>
            <Typography variant="h6" gutterBottom>Actividad de Usuarios por Mes</Typography>
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <CircularProgress />
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getMonth() + 1}/${date.getFullYear()}`;
                        }} />
                        <YAxis />
                        <Tooltip labelFormatter={(value) => {
                            const date = new Date(value);
                            return `Mes: ${date.getMonth() + 1}/${date.getFullYear()}`;
                        }} />
                        <Legend />
                        <Bar dataKey="webcountmonth" fill="#8884d8" name="Usuarios Web por Mes" />
                        <Bar dataKey="gafascountmonth" fill="#82ca9d" name="Usuarios Meta por Mes" />
                        <Bar dataKey="democountmonth" fill="#ff7300" name="Usuarios Demo por Mes" />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </CardContent>
    </Card>
);


// /Users/caffas/Documents/Sistemas/vsadmin/admin/admin-app/src/Dashboard.js

const LogDayActivitySourceChart = ({ data, isLoading }) => {
    const [selectedSources, setSelectedSources] = useState([]);
    
    const getSourceColor = (source, index) => {
        const colors = [
            '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', 
            '#F44336', '#00BCD4', '#8BC34A', '#FFC107',
            '#E91E63', '#607D8B', '#795548', '#3F51B5'
        ];
        return colors[index % colors.length];
    };

    // Procesar datos para el gráfico PRIMERO
    const processedData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        const groupedByDate = data.reduce((acc, item) => {
            const date = item.month || item.fecha;
            const source = item.source || item.fuente || 'Unknown';
            if (!acc[date]) acc[date] = { month: date };
            acc[date][`source_${source}`] = (acc[date][`source_${source}`] || 0) + (parseInt(item.countmonth) || parseInt(item.count) || 1);
            return acc;
        }, {});
        return Object.values(groupedByDate).sort((a, b) => new Date(a.month) - new Date(b.month));
    }, [data]);

    // Obtener fuentes únicas de los datos PROCESADOS (no de los datos originales)
    const uniqueSources = React.useMemo(() => {
        if (!processedData || processedData.length === 0) return [];
        const sourcesSet = new Set();
        
        // Extraer todas las fuentes de los datos procesados
        processedData.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key.startsWith('source_') && item[key] > 0) {
                    const source = key.replace('source_', '');
                    sourcesSet.add(source);
                }
            });
        });
        
        return Array.from(sourcesSet).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }, [processedData]);

    // Filtrar datos procesados según las fuentes seleccionadas
    const filteredProcessedData = React.useMemo(() => {
        if (!processedData || processedData.length === 0) return [];
        if (selectedSources.length === 0) return processedData; // Si no hay selección, mostrar todo
        
        return processedData.map(item => {
            const filteredItem = { month: item.month };
            selectedSources.forEach(source => {
                const key = `source_${source}`;
                if (item[key]) {
                    filteredItem[key] = item[key];
                }
            });
            return filteredItem;
        });
    }, [processedData, selectedSources]);

    // Fuentes para mostrar en el gráfico (solo las seleccionadas o todas si no hay selección)
    const sourcesForChart = React.useMemo(() => {
        if (selectedSources.length === 0) return uniqueSources;
        return selectedSources.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }, [uniqueSources, selectedSources]);

    const handleSourceChange = (event) => {
        const value = event.target.value;
        setSelectedSources(typeof value === 'string' ? value.split(',') : value);
    };

    const handleClearSelection = () => {
        setSelectedSources([]);
    };

    return (
        <Card sx={{ mt: 4, p: { xs: 1, sm: 2 }, boxShadow: 3 }}>
            <CardContent>
                <Box 
                    display="flex" 
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between" 
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    mb={3}
                    gap={2}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                        Actividad de Logs por Fuente y Día
                    </Typography>
                    
                    <FormControl sx={{ minWidth: { xs: '100%', sm: 250 } }} size="small">
                        <InputLabel id="source-filter-label">
                            <Box display="flex" alignItems="center">
                                <FilterListIcon sx={{ mr: 1, fontSize: 16 }} />
                                Filtrar por Fuente ({uniqueSources.length})
                            </Box>
                        </InputLabel>
                        <Select
                            labelId="source-filter-label"
                            multiple
                            value={selectedSources}
                            onChange={handleSourceChange}
                            label={`Filtrar por Fuente (${uniqueSources.length})`}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.length === 0 ? (
                                        <em>Todas las fuentes</em>
                                    ) : selected.length <= 2 ? (
                                        selected.map((value) => (
                                            <Chip key={value} label={value} size="small" />
                                        ))
                                    ) : (
                                        <Chip 
                                            label={`${selected.length} fuentes seleccionadas`} 
                                            size="small" 
                                            color="primary"
                                        />
                                    )}
                                </Box>
                            )}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        width: 250,
                                    },
                                },
                            }}
                        >
                            {uniqueSources.map((source) => (
                                <MenuItem key={source} value={source}>
                                    <Box display="flex" alignItems="center" width="100%">
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: getSourceColor(source, uniqueSources.indexOf(source)),
                                                mr: 1,
                                                flexShrink: 0
                                            }}
                                        />
                                        <Typography variant="body2" noWrap>
                                            {source}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {selectedSources.length > 0 && (
                    <Box mb={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                        <Typography variant="body2" color="textSecondary">
                            Mostrando:
                        </Typography>
                        {selectedSources.map((source) => (
                            <Chip 
                                key={source}
                                label={source}
                                color="primary"
                                variant="outlined"
                                size="small"
                                onDelete={() => {
                                    setSelectedSources(prev => prev.filter(s => s !== source));
                                }}
                            />
                        ))}
                        <Button
                            size="small"
                            onClick={handleClearSelection}
                            sx={{ ml: 1 }}
                        >
                            Limpiar todo
                        </Button>
                    </Box>
                )}

                {/* Información de debug para verificar datos */}
                {process.env.NODE_ENV === 'development' && (
                    <Box mb={2} p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                            Debug: {uniqueSources.length} fuentes encontradas: {uniqueSources.join(', ')}
                        </Typography>
                    </Box>
                )}

                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <CircularProgress />
                    </Box>
                ) : filteredProcessedData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <Typography variant="body2" color="textSecondary">
                            No hay datos disponibles para las fuentes seleccionadas
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveChartContainer height={window.innerWidth < 600 ? 250 : 350}>
                        <BarChart 
                            data={filteredProcessedData} 
                            margin={{ 
                                top: 5, 
                                right: window.innerWidth < 600 ? 10 : 30, 
                                left: window.innerWidth < 600 ? 10 : 20, 
                                bottom: 5 
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="month" 
                                tickFormatter={(value) => {
                                    const date = new Date(value);
                                    return `${date.getDate()}/${date.getMonth() + 1}`;
                                }}
                                fontSize={window.innerWidth < 600 ? 10 : 12}
                                interval={window.innerWidth < 600 ? 1 : 0}
                            />
                            <YAxis fontSize={window.innerWidth < 600 ? 10 : 12} />
                            <Tooltip 
                                labelFormatter={(value) => {
                                    const date = new Date(value);
                                    return `Fecha: ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
                                }} 
                                formatter={(value, name) => {
                                    const sourceName = name.replace('source_', '');
                                    return [value, sourceName];
                                }}
                                contentStyle={{ fontSize: window.innerWidth < 600 ? '12px' : '14px' }}
                            />
                            <Legend 
                                formatter={(value) => value.replace('source_', '')}
                                wrapperStyle={{ fontSize: window.innerWidth < 600 ? '12px' : '14px' }}
                            />
                            {sourcesForChart.map((source, index) => (
                                <Bar 
                                    key={source}
                                    dataKey={`source_${source}`} 
                                    fill={getSourceColor(source, index)} 
                                    name={`source_${source}`}
                                    stackId="sources"
                                />
                            ))}
                        </BarChart>
                    </ResponsiveChartContainer>
                )}
            </CardContent>
        </Card>
    );
};

const LogDayActivitySourceUniqueChart = ({ data, isLoading }) => {
    const [selectedSources, setSelectedSources] = useState([]);
    
    const getSourceColor = (source, index) => {
        const colors = [
            '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', 
            '#F44336', '#00BCD4', '#8BC34A', '#FFC107',
            '#E91E63', '#607D8B', '#795548', '#3F51B5'
        ];
        return colors[index % colors.length];
    };

    // Procesar datos para el gráfico PRIMERO
    const processedData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        const groupedByDate = data.reduce((acc, item) => {
            const date = item.month || item.fecha;
            const source = item.source || item.fuente || 'Unknown';
            if (!acc[date]) acc[date] = { month: date };
            acc[date][`source_${source}`] = (acc[date][`source_${source}`] || 0) + (parseInt(item.countmonth) || parseInt(item.count) || 1);
            return acc;
        }, {});
        return Object.values(groupedByDate).sort((a, b) => new Date(a.month) - new Date(b.month));
    }, [data]);

    // Obtener fuentes únicas de los datos PROCESADOS (no de los datos originales)
    const uniqueSources = React.useMemo(() => {
        if (!processedData || processedData.length === 0) return [];
        const sourcesSet = new Set();
        
        // Extraer todas las fuentes de los datos procesados
        processedData.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key.startsWith('source_') && item[key] > 0) {
                    const source = key.replace('source_', '');
                    sourcesSet.add(source);
                }
            });
        });
        
        return Array.from(sourcesSet).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }, [processedData]);

    // Filtrar datos procesados según las fuentes seleccionadas
    const filteredProcessedData = React.useMemo(() => {
        if (!processedData || processedData.length === 0) return [];
        if (selectedSources.length === 0) return processedData; // Si no hay selección, mostrar todo
        
        return processedData.map(item => {
            const filteredItem = { month: item.month };
            selectedSources.forEach(source => {
                const key = `source_${source}`;
                if (item[key]) {
                    filteredItem[key] = item[key];
                }
            });
            return filteredItem;
        });
    }, [processedData, selectedSources]);

    // Fuentes para mostrar en el gráfico (solo las seleccionadas o todas si no hay selección)
    const sourcesForChart = React.useMemo(() => {
        if (selectedSources.length === 0) return uniqueSources;
        return selectedSources.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }, [uniqueSources, selectedSources]);

    const handleSourceChange = (event) => {
        const value = event.target.value;
        setSelectedSources(typeof value === 'string' ? value.split(',') : value);
    };

    const handleClearSelection = () => {
        setSelectedSources([]);
    };

    return (
        <Card sx={{ mt: 4, p: { xs: 1, sm: 2 }, boxShadow: 3 }}>
            <CardContent>
                <Box 
                    display="flex" 
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between" 
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    mb={3}
                    gap={2}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                        Usuarios Únicos por Fuente y Día
                    </Typography>
                    
                    <FormControl sx={{ minWidth: { xs: '100%', sm: 250 } }} size="small">
                        <InputLabel id="source-unique-filter-label">
                            <Box display="flex" alignItems="center">
                                <FilterListIcon sx={{ mr: 1, fontSize: 16 }} />
                                Filtrar por Fuente ({uniqueSources.length})
                            </Box>
                        </InputLabel>
                        <Select
                            labelId="source-unique-filter-label"
                            multiple
                            value={selectedSources}
                            onChange={handleSourceChange}
                            label={`Filtrar por Fuente (${uniqueSources.length})`}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.length === 0 ? (
                                        <em>Todas las fuentes</em>
                                    ) : selected.length <= 2 ? (
                                        selected.map((value) => (
                                            <Chip key={value} label={value} size="small" />
                                        ))
                                    ) : (
                                        <Chip 
                                            label={`${selected.length} fuentes seleccionadas`} 
                                            size="small" 
                                            color="primary"
                                        />
                                    )}
                                </Box>
                            )}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        width: 250,
                                    },
                                },
                            }}
                        >
                            {uniqueSources.map((source) => (
                                <MenuItem key={source} value={source}>
                                    <Box display="flex" alignItems="center" width="100%">
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: getSourceColor(source, uniqueSources.indexOf(source)),
                                                mr: 1,
                                                flexShrink: 0
                                            }}
                                        />
                                        <Typography variant="body2" noWrap>
                                            {source}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {selectedSources.length > 0 && (
                    <Box mb={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                        <Typography variant="body2" color="textSecondary">
                            Mostrando:
                        </Typography>
                        {selectedSources.map((source) => (
                            <Chip 
                                key={source}
                                label={source}
                                color="primary"
                                variant="outlined"
                                size="small"
                                onDelete={() => {
                                    setSelectedSources(prev => prev.filter(s => s !== source));
                                }}
                            />
                        ))}
                        <Button
                            size="small"
                            onClick={handleClearSelection}
                            sx={{ ml: 1 }}
                        >
                            Limpiar todo
                        </Button>
                    </Box>
                )}

                {/* Información de debug para verificar datos */}
                {process.env.NODE_ENV === 'development' && (
                    <Box mb={2} p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                            Debug: {uniqueSources.length} fuentes encontradas: {uniqueSources.join(', ')}
                        </Typography>
                    </Box>
                )}

                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <CircularProgress />
                    </Box>
                ) : filteredProcessedData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <Typography variant="body2" color="textSecondary">
                            No hay datos disponibles para las fuentes seleccionadas
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveChartContainer height={window.innerWidth < 600 ? 250 : 350}>
                        <BarChart 
                            data={filteredProcessedData} 
                            margin={{ 
                                top: 5, 
                                right: window.innerWidth < 600 ? 10 : 30, 
                                left: window.innerWidth < 600 ? 10 : 20, 
                                bottom: 5 
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="month" 
                                tickFormatter={(value) => {
                                    const date = new Date(value);
                                    return `${date.getDate()}/${date.getMonth() + 1}`;
                                }}
                                fontSize={window.innerWidth < 600 ? 10 : 12}
                                interval={window.innerWidth < 600 ? 1 : 0}
                            />
                            <YAxis fontSize={window.innerWidth < 600 ? 10 : 12} />
                            <Tooltip 
                                labelFormatter={(value) => {
                                    const date = new Date(value);
                                    return `Fecha: ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
                                }} 
                                formatter={(value, name) => {
                                    const sourceName = name.replace('source_', '');
                                    return [value, sourceName];
                                }}
                                contentStyle={{ fontSize: window.innerWidth < 600 ? '12px' : '14px' }}
                            />
                            <Legend 
                                formatter={(value) => value.replace('source_', '')}
                                wrapperStyle={{ fontSize: window.innerWidth < 600 ? '12px' : '14px' }}
                            />
                            {sourcesForChart.map((source, index) => (
                                <Bar 
                                    key={source}
                                    dataKey={`source_${source}`} 
                                    fill={getSourceColor(source, index)} 
                                    name={`source_${source}`}
                                    stackId="sources"
                                />
                            ))}
                        </BarChart>
                    </ResponsiveChartContainer>
                )}
            </CardContent>
        </Card>
    );
};

// ... rest of code remains same

const Dashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const isMountedRef = useRef(true);
    const [kpiData, setKpiData] = useState({
        usuariosWeb: '0', usuariosMeta: '0', usuariosDiaWeb: '0', webcountMonth: '0', porcentajeWeb: '0%', porcentajeMeta: '0%', supportTotal: '0', feedbackTotal: '0', proyectosTotal: '0', sourceTotal: '0', tipoLicenciaTotal: '0', demoTotal: '0'
    });
    const [chartData, setChartData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [monthlyChartData, setMonthlyChartData] = useState([]);
    const [sourceData, setSourceData] = useState([]);
    const [tipoLicenciaData, setTipoLicenciaData] = useState([]);
    const [logDayActivityData, setLogDayActivityData] = useState([]);
    const [logDayActivitySourceData, setLogDayActivitySourceData] = useState([]);
    const [logDayActivityStatusData, setLogDayActivityStatusData] = useState([]);
    const [logDayActivityUsersData, setLogDayActivityUsersData] = useState([]);
    const [recentUsers, setRecentUsers] = useState([]);
    const [logSourceData, setLogSourceData] = useState([]);
    const [logDayActivitySourceUniqueData, setLogDayActivitySourceUniqueData] = useState([]);


    const getLatestLogActivityUsers = useCallback(() => {
        if (!logDayActivityUsersData || logDayActivityUsersData.length === 0) {
            return { count: '0', date: 'Sin datos' };
        }
        const latestEntry = logDayActivityUsersData[logDayActivityUsersData.length - 1];
        return { count: latestEntry.countmonth.toString(), date: latestEntry.month };
    }, [logDayActivityUsersData]);

    const isToday = useCallback((dateStr) => {
        const today = new Date();
        const date = new Date(dateStr);
        return (date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear());
    }, []);

    const formatDate = useCallback((dateStr) => {
        if (!dateStr || dateStr === 'Sin datos' || dateStr === 'Error al cargar') return 'No hay datos disponibles';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return 'Fecha inválida';
            return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
        } catch (error) {
            console.error('Error al formatear fecha:', error);
            return 'Error de formato';
        }
    }, []);

    const fetchRecentUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No hay token de autenticación');
            
            const API_URL = window.location.hostname === 'localhost' 
                ? 'http://localhost:3000'
                : 'https://98fzfcf5hk.execute-api.eu-west-3.amazonaws.com/v2';

            const url = new URL(`${API_URL}/users`);
            url.searchParams.append('per_page', '10');
            url.searchParams.append('page', '1');
            url.searchParams.append('sort_by', 'subscriptionstarttime');
            url.searchParams.append('order', 'asc');
            url.searchParams.append('source', "'visualscaleVR'");
            url.searchParams.append('subscription_status', "'active'");
            
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error(`Error: ${response.status}`);
            const responseData = await response.json();
            if (responseData.data) {
                const usersData = responseData.data.slice(0, 10).map(user => ({
                    id: user.id, name: user.name || user.username, email: user.email, tipotrial: user.tipotrial, created_at: user.subscriptionstarttime, source: user.source || user.fuente || 'Web'
                }));
                setRecentUsers(usersData);
            } else {
                console.warn('No se pudieron obtener los usuarios recientes');
                setRecentUsers([]);
            }
        } catch (error) {
            console.error('Error al obtener usuarios recientes:', error);
            setRecentUsers([]);
        }
    };

    const fetchDashboardData = async () => {
        if (!isMountedRef.current) return;
        console.log('✅  fetchDashboardData called');
        setIsLoading(true);
        setRefreshing(true);
        try {
            await Promise.all([fetchDashboardKPIs(), fetchRecentUsers()]);
        } catch (error) {
            console.error('🚨 Error al obtener datos del dashboard:', error);
        } finally {
            setIsLoading(false);
            setRefreshing(false);
        }
    };

    const fetchDashboardKPIs = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('🚨 No hay token de autenticación');
            //const API_URL = 'https://98fzfcf5hk.execute-api.eu-west-3.amazonaws.com/v2';
                        // Cambiar la definición de API_URL para que dependa del entorno
            const API_URL = window.location.hostname === 'localhost' 
                    ? 'http://localhost:3000'
                    : 'https://98fzfcf5hk.execute-api.eu-west-3.amazonaws.com/v2';

            const url = new URL(`${API_URL}/kpi`);
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error(`🚨 Error: ${response.status}`);
            const responseData = await response.json();
            if (!responseData.success) throw new Error('🚨 La API no devolvió datos exitosos');
            const data = responseData.data;

            const totalUsuarios = parseInt(data.webCountTotal || 0) + parseInt(data.gafasCountTotal || 0);
            const porcentajeWeb = totalUsuarios > 0 ? Math.round((parseInt(data.webCountTotal || 0) / totalUsuarios) * 100) : 0;
            const porcentajeMeta = totalUsuarios > 0 ? Math.round((parseInt(data.gafasCountTotal || 0) / totalUsuarios) * 100) : 0;

            let logActivityUsersData = [];
            if (data.logDayActivityUsers && Array.isArray(data.logDayActivityUsers)) {
                logActivityUsersData = data.logDayActivityUsers.map(item => ({
                    month: item.month, countmonth: parseInt(item.countmonth) || 0
                })).sort((a, b) => new Date(a.month) - new Date(b.month));
            }
            setLogDayActivityUsersData(logActivityUsersData);

            let logActivityData = [];
            if (data.logDayActivity && Array.isArray(data.logDayActivity)) {
                logActivityData = data.logDayActivity.map(item => ({
                    month: item.month, countmonth: parseInt(item.countmonth) || 0
                })).sort((a, b) => new Date(a.month) - new Date(b.month));
            }
            setLogDayActivityData(logActivityData);

            let logActivitySourceData = [];
            if (data.logDayActivitySource && Array.isArray(data.logDayActivitySource)) {
                logActivitySourceData = data.logDayActivitySource.map(item => ({
                    month: item.month, source: item.source || 'Desconocido', countmonth: parseInt(item.countmonth) || 0
                })).sort((a, b) => new Date(a.month) - new Date(b.month));
            }
            setLogDayActivitySourceData(logActivitySourceData);

            let logActivityStatusData = [];
            if (data.logDayActivityStatus && Array.isArray(data.logDayActivityStatus)) {
                logActivityStatusData = data.logDayActivityStatus.map(item => ({
                    month: item.month, status: item.status || 'Unknown', countmonth: parseInt(item.countmonth) || 0
                })).sort((a, b) => new Date(a.month) - new Date(b.month));
            }
            setLogDayActivityStatusData(logActivityStatusData);

            let lastUpdateDate = 'Sin datos';
            let sourcesData = [];
            if (data.sourceTotal && Array.isArray(data.sourceTotal)) {
                sourcesData = data.sourceTotal.map(item => ({
                    source: item.source || 'Desconocido', count: parseInt(item.count) || 0
                }));
            }
            setSourceData(sourcesData);

            let licenciaData = [];
            if (data.tipoLicenciaTotal && Array.isArray(data.tipoLicenciaTotal)) {
                licenciaData = data.tipoLicenciaTotal.map(item => ({
                    tipoLicencia: item.tipotrial || 'Desconocido', count: parseInt(item.count) || 0
                }));
            }
            setTipoLicenciaData(licenciaData);
            
            let logActivitySourceUniqueData = [];
            if (data.logDayActivitySourceUnique && Array.isArray(data.logDayActivitySourceUnique)) {
                logActivitySourceUniqueData = data.logDayActivitySourceUnique.map(item => ({
                    month: item.month,
                    source: item.source || 'Desconocido',
                    countmonth: parseInt(item.countmonth) || 0
                })).sort((a, b) => new Date(a.month) - new Date(b.month));
            }
            setLogDayActivitySourceUniqueData(logActivitySourceUniqueData);

            const combinedChartData = [];
            const webDataMap = {};
            if (data.webcountDay && Array.isArray(data.webcountDay)) {
                data.webcountDay.forEach(item => {
                    const dateStr = item.day.split('T')[0];
                    webDataMap[dateStr] = item.countmonth;
                });
            }
           

            const monthlyData = [];
            const webMonthlyMap = {};
            if (data.webcountMonth && Array.isArray(data.webcountMonth)) {
                data.webcountMonth.forEach(item => {
                    const monthStr = item.month.split('T')[0];
                    webMonthlyMap[monthStr] = item.countmonth;
                });
            }
            const gafasMonthlyMap = {};
            if (data.gafasTotalMonth && Array.isArray(data.gafasTotalMonth)) {
                data.gafasTotalMonth.forEach(item => {
                    const monthStr = item.month.split('T')[0];
                    gafasMonthlyMap[monthStr] = item.countmonth;
                });
            }
            const demoMonthlyMap = {};
            if (data.democountMonth && Array.isArray(data.democountMonth)) {
                data.democountMonth.forEach(item => {
                    const monthStr = item.month.split('T')[0];
                    demoMonthlyMap[monthStr] = item.countmonth;
                });
            }
            const allMonths = [...new Set([...Object.keys(webMonthlyMap), ...Object.keys(gafasMonthlyMap), ...Object.keys(demoMonthlyMap)])].sort();
            allMonths.forEach(monthStr => {
                monthlyData.push({
                    month: monthStr, webcountmonth: webMonthlyMap[monthStr] || '0', gafascountmonth: gafasMonthlyMap[monthStr] || '0', democountmonth: demoMonthlyMap[monthStr] || '0'
                });
            });
            setMonthlyChartData(monthlyData);

            const gafasDataMap = {};
            if (data.gafasCountDay && Array.isArray(data.gafasCountDay)) {
                data.gafasCountDay.forEach(item => {
                    const dateStr = item.day.split('T')[0];
                    gafasDataMap[dateStr] = item.countmonth;
                });
            }
            const allDates = [...new Set([...Object.keys(webDataMap), ...Object.keys(gafasDataMap)])].sort();
            const recentDates = allDates.slice(-30);
            recentDates.forEach(dateStr => {
                combinedChartData.push({
                    day: dateStr, webcountmonth: webDataMap[dateStr] || '0', gafascountmonth: gafasDataMap[dateStr] || '0', democountmonth: demoMonthlyMap[dateStr] || '0'
                });
            });
            setChartData(combinedChartData);
            
            if (allDates.length > 0) {
                const dateTimestamps = allDates.map(dateStr => new Date(dateStr).getTime());
                const maxTimestamp = Math.max(...dateTimestamps);
                const mostRecentDate = new Date(maxTimestamp);
                if (!isNaN(mostRecentDate.getTime())) {
                    lastUpdateDate = mostRecentDate.toISOString().split('T')[0];
                } else {
                    lastUpdateDate = 'Fecha inválida';
                }
            }

            setKpiData({
                usuariosWeb: data.webCountTotal || '0', usuariosMeta: data.gafasCountTotal || '0', usuariosDiaWeb: data.webcountDay && data.webcountDay[0] ? data.webcountDay[0].countmonth : '0', webcountMonth: data.webcountMonth && data.webcountMonth[0] ? data.webcountMonth[0].countmonth : '0', porcentajeWeb: `${porcentajeWeb}%`, porcentajeMeta: `${porcentajeMeta}%`, supportTotal: data.supportTotal || '0', feedbackTotal: data.feedbackTotal || '0', proyectosTotal: data.proyectosTotal || '0', lastUpdateDate: lastUpdateDate, sourceTotal: data.sourceTotal || '0', tipoLicenciaTotal: data.tipoLicenciaTotal || '0', priceTotal: data.priceTotal ? parseFloat(data.priceTotal).toFixed(2) : '0.00', demoTotal: data.demoTotal || '0'
            });
        } catch (error) {
            console.error('🚨 Error al obtener KPIs del dashboard:', error);
            setKpiData({
                usuariosWeb: '0', usuariosMeta: '0', usuariosDiaWeb: '0', webcountMonth: '0', porcentajeWeb: '0%', porcentajeMeta: '0%', feedbackTotal: '0', supportTotal: '0', proyectosTotal: '0', lastUpdateDate: 'Error al cargar', sourceTotal: '0', tipoLicenciaTotal: '0', priceTotal: '0', demoTotal: '0'
            });
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        const userAccount = localStorage.getItem('userAccount');
        if (!token || !userAccount) {
            console.error('🚨 Usuario no autenticado correctamente');
            return;
        }
        fetchDashboardData();
        return () => { isMountedRef.current = false; };
    }, []);

    return (
        <Box sx={{ flexGrow: 1, padding: 2 }}>
            <Card sx={{ mb: 4 }}>
                <Title title="Dashboard" />
                <CardContent>
                    <Typography variant="h4" gutterBottom>Bienvenido al Panel de Administración de Visual Scale</Typography>
                    <Typography variant="body1">Este dashboard muestra la última información de la plataforma como cantidad de usuarios, registros, etc.</Typography>
                </CardContent>
            </Card>
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>KPI Generales</Typography>
            {isLoading ? (
                <Box display="flex" justifyContent="center" my={5}><CircularProgress /></Box>
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={6} sm={6} md={3}><KpiCard icon={<AttachMoneyIcon sx={{ color: 'primary.main' }} />} title="Total Payment" value={kpiData.priceTotal} /></Grid>
                    <Grid item xs={6} sm={6} md={3}><KpiCard icon={<PeopleIcon sx={{ color: 'primary.main' }} />} title="Web" value={kpiData.usuariosWeb} /></Grid>
                    <Grid item xs={6} sm={6} md={3}><KpiCard icon={<DevicesIcon sx={{ color: 'secondary.main' }} />} title="Meta" value={kpiData.usuariosMeta} /></Grid>
                    <Grid item xs={6} sm={6} md={3}><KpiCard icon={<DemoIcon sx={{ color: 'warning.main' }} />} title="Demo Users" value={kpiData.demoTotal} /></Grid>
                    <Grid item xs={6} sm={6} md={3}><KpiCard icon={<TodayIcon sx={{ color: 'info.main' }} />} title="Registros Web Hoy" value={isToday(kpiData.lastUpdateDate) ? kpiData.usuariosDiaWeb : "0"} /></Grid>
                    <Grid item xs={6} sm={6} md={2}><KpiCard icon={<PercentIcon sx={{ color: 'success.main' }} />} title="Web" value={kpiData.porcentajeWeb} /></Grid>
                    <Grid item xs={6} sm={6} md={2}><KpiCard icon={<PercentIcon sx={{ color: 'warning.main' }} />} title="Meta" value={kpiData.porcentajeMeta} /></Grid>
                    <Grid item xs={6} sm={6} md={2}><KpiCard icon={<ProjectIcon sx={{ color: 'info.main' }} />} title="# Proyectos" value={kpiData.proyectosTotal} /></Grid>
                    <Grid item xs={6} sm={6} md={2}><KpiCard icon={<SupportIcon sx={{ color: 'info.main' }} />} title="Support" value={kpiData.supportTotal} /></Grid>
                    <Grid item xs={6} sm={6} md={2}><KpiCard icon={<FeedbackIcon sx={{ color: 'info.main' }} />} title="Feedback" value={kpiData.feedbackTotal} /></Grid>
                    <Grid item xs={6} sm={6} md={4}><KpiCard icon={<UpdateIcon sx={{ color: 'info.main' }} />} title="Last Registration Date" value={formatDate(kpiData.lastUpdateDate)} /></Grid>
                    <Grid item xs={6} sm={6} md={4}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 3 }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Box sx={{ borderRadius: '50%', p: 1, mr: 2 }}><PeopleIcon sx={{ color: 'primary.main' }} /></Box>
                                    <Box>
                                        <Typography variant="h6" color="textSecondary">Usuarios Access (Último Día)</Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>{formatDate(getLatestLogActivityUsers().date)}</Typography>
                                    </Box>
                                </Box>
                                <Typography variant="h4" component="div" fontWeight="bold">{getLatestLogActivityUsers().count}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
            <RecentUsersTable data={recentUsers} isLoading={isLoading} />
            <UserActivityChart data={chartData} isLoading={isLoading} />
            <MonthlyUserChart data={monthlyChartData} isLoading={isLoading} />
            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}><SourceDonutChart data={sourceData} isLoading={isLoading} /></Grid>
                <Grid item xs={12} md={6}><TipoLicenciaChart data={tipoLicenciaData} isLoading={isLoading} /></Grid>
            </Grid>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} lg={6}>
                    <LogDayActivityUsersChart data={logDayActivityUsersData} isLoading={isLoading} />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <LogDayActivityChart data={logDayActivityData} isLoading={isLoading} />
                </Grid>
            </Grid>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} lg={6}>
                    <LogDayActivitySourceChart data={logDayActivitySourceData} isLoading={isLoading} />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <LogDayActivitySourceUniqueChart data={logDayActivitySourceUniqueData} isLoading={isLoading} />
                </Grid>
            </Grid>
            

            <LogDayActivityStatusChart data={logDayActivityStatusData} isLoading={isLoading} />
            

            {/* Botón de actualizar con mejor espaciado */}
            <Box 
                mt={3} 
                display="flex" 
                justifyContent="center" 
                alignItems="center"
                px={{ xs: 2, sm: 0 }}
            >
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />} 
                    onClick={fetchDashboardData} 
                    disabled={refreshing} 
                    fullWidth={isMobile}
                    sx={{ 
                        borderRadius: 2, 
                        px: 3, 
                        py: 1, 
                        boxShadow: 3,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        maxWidth: { xs: '100%', sm: 'auto' },
                        '&:hover': { 
                            boxShadow: 5, 
                            transform: 'translateY(-2px)', 
                            transition: 'all 0.3s' 
                        } 
                    }}
                >
                    {refreshing ? 'Actualizando...' : 'Actualizar datos'}
                </Button>
            </Box>
            
        </Box>
    );
};

export default Dashboard;